/**
 * Map Header component - Contains data selector, home icon, undo, and expand/collapse
 */

import { Component, Show } from 'solid-js';
import type { DataSet } from '../types/interfaces';
import DataSetSelector from './DataSetSelector';
import { TooltipDelayed } from '../../tooltipDelayed/TooltipDelayed';

interface MapHeaderProps {
  dataSets?: DataSet[];
  currentDataSetId?: string;
  homeIconName?: string;
  canMove: boolean;
  canShowHome: boolean;
  canShowHeaderReset: boolean;
  canExpand: boolean;
  isExpanded: boolean;
  onDataSetChange?(dataSetId: string): void;
  onHomeIconClick(e: MouseEvent): void;
  onUndo(e: MouseEvent): void;
  onExpandCollapse(e: MouseEvent): void;
  getAssetIcon(iconName: string): string | null;
}

const MapHeader: Component<MapHeaderProps> = (props) => {
  return (
    <div
      class={`font-medium text-sm flex items-center relative bg-white text-[var(--modus-wc-color-base-content,#171C1E)] w-full h-10 justify-between opacity-100 p-2 gap-1 rounded-t-lg border-b border-gray-0 ${
        props.canMove ? 'cursor-move select-none' : 'cursor-default select-auto'
      }`}
    >
      {/* Left Section - Flexible Dropdown */}
      <div class="flex items-center gap-1 flex-1 min-w-0">
        <DataSetSelector
          dataSets={props.dataSets}
          currentDataSetId={props.currentDataSetId}
          onDataSetChange={props.onDataSetChange}
        />
      </div>

      {/* Separator Line */}
      <div
        class="w-5 h-0 rotate-[-90deg] opacity-100 border-2 border-gray-light"
        style={{
          width: '20px',
          height: '0px',
          transform: 'rotate(-90deg)',
        }}
      />

      {/* Right Section - Fixed Icons */}
      <div class="flex items-center space-x-1 flex-shrink-0">
        {/* Home/Navigation Icon */}
        <Show when={props.canShowHome}>
          <TooltipDelayed text="Home" position="bottom">
            <button
              onClick={(e) => {
                props.onHomeIconClick(e);
              }}
              class="flex items-center justify-center w-6 h-6 hover:bg-black/5 rounded transition-colors"
            >
              {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
              {(() => {
                const iconName = props.homeIconName || 'home';
                const assetIcon = props.getAssetIcon(iconName);

                return (
                  <Show
                    when={assetIcon}
                    fallback={
                      <span
                        class="tc-icon-unknown icon-font"
                        aria-hidden="true"
                        style={{
                          width: '16px',
                          height: '16px',
                          ['font-size']: '16px',
                        }}
                      ></span>
                    }
                  >
                    <img src={assetIcon!} alt={iconName} class="w-5 h-5" />
                  </Show>
                );
              })()}
            </button>
          </TooltipDelayed>
        </Show>

        {/* Undo Icon */}
        <Show when={props.canShowHeaderReset}>
          <TooltipDelayed
            text="Reset map to default position and size"
            position="bottom"
          >
            <button
              onClick={(e) => {
                props.onUndo(e);
              }}
              class="flex items-center justify-center w-6 h-6 hover:bg-black/5 rounded transition-colors"
            >
              <span
                class="tc-icon-action-undo icon-font"
                aria-hidden="true"
                style={{
                  width: '16px',
                  height: '16px',
                  ['font-size']: '16px',
                }}
              ></span>
            </button>
          </TooltipDelayed>
        </Show>

        {/* Collapse/Expand Button */}
        <Show when={props.canExpand}>
          <TooltipDelayed
            text={
              props.isExpanded
                ? 'Collapse to original size'
                : 'Expand to full view'
            }
            position="bottom"
          >
            <button
              onClick={(e) => {
                props.onExpandCollapse(e);
              }}
              class="flex items-center justify-center w-6 h-6 hover:bg-black/5 rounded transition-colors"
            >
              <span
                class={`tc-icon-${props.isExpanded ? 'fullscreen-exit' : 'fullscreen'} icon-font`}
                aria-hidden="true"
                style={{
                  width: '16px',
                  height: '16px',
                  ['font-size']: '16px',
                }}
              ></span>
            </button>
          </TooltipDelayed>
        </Show>
      </div>
    </div>
  );
};

export default MapHeader;
